import React, { useMemo } from 'react';
import { Badge, Card, Divider, ScrollArea, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiPage, {
  ChartCard,
  ChartTooltip,
  QueryState,
  StatTile,
  fmtInt,
  fmtMoney,
  fmtPct,
  useChartColors,
} from '../KpiPage';
import { kpiBySlug } from '../kpiCatalog';
import { useWindowParams } from '../DateWindowSelector';
import useNexusQuery, { field, num } from '../../../hooks/useNexusQuery';

const kpi = kpiBySlug('repair-expected-vs-actual');

function SectionHeading({ title, subtitle }) {
  return (
    <Stack gap={4} mt="lg">
      <Text fw={800} size="xl">
        {title}
      </Text>
      {subtitle && (
        <Text size="sm" c="dimmed">
          {subtitle}
        </Text>
      )}
      <Divider mt={2} />
    </Stack>
  );
}

export default function RepairExpectedVsActual() {
  const c = useChartColors();
  const [window, setWindow] = useWindowParams();

  const benchmark = useNexusQuery('initiative-repair-vendor-benchmark', window);
  const conflicts = useNexusQuery('initiative-repair-conflicting-signals', window);
  const tat = useNexusQuery('initiative-repair-tat-variance', window);

  // --- Section 1: pricing benchmark ---
  const vendors = useMemo(
    () =>
      benchmark.rows.map((r) => ({
        vendor: field(r, 'Vendor'),
        repairs: num(r, 'ComparableRepairs'),
        avgDev: num(r, 'AvgDeviationPct'),
        high: num(r, 'HighOutliers'),
        low: num(r, 'LowOutliers'),
        dollarVs: num(r, 'DollarVsBenchmark'),
      })),
    [benchmark.rows]
  );

  const benchTotals = useMemo(() => {
    const repairs = vendors.reduce((s, v) => s + v.repairs, 0);
    const flagged = vendors.reduce((s, v) => s + v.high + v.low, 0);
    const dollarsOver = vendors.reduce((s, v) => s + Math.max(0, v.dollarVs), 0);
    return { vendorCount: vendors.length, repairs, flagged, dollarsOver };
  }, [vendors]);

  // Top vendors by comparable volume, then drawn most-over-benchmark first.
  const devByVendor = useMemo(
    () =>
      [...vendors]
        .sort((a, b) => b.repairs - a.repairs)
        .slice(0, 15)
        .sort((a, b) => b.avgDev - a.avgDev),
    [vendors]
  );

  const conflictRows = useMemo(
    () =>
      conflicts.rows.map((r) => ({
        vendor: field(r, 'Vendor'),
        part: field(r, 'Partnumber'),
        desc: field(r, 'Description'),
        cond: field(r, 'Condition'),
        vendorCost: num(r, 'VendorUnitCost'),
        benchmark: num(r, 'BenchmarkPrice'),
        dev: num(r, 'DeviationPct'),
        priced: num(r, 'VendorsPriced'),
        minP: num(r, 'MinPrice'),
        maxP: num(r, 'MaxPrice'),
        when: field(r, 'WhenSold'),
      })),
    [conflicts.rows]
  );

  // --- Section 3: turn-time reliability (secondary) ---
  const tatRows = useMemo(
    () =>
      tat.rows.map((r) => ({
        vendor: (field(r, 'Vendor') || '').slice(0, 18),
        promised: Math.round(num(r, 'AvgPromisedDays')),
        actual: Math.round(num(r, 'AvgActualDays')),
        variance: num(r, 'AvgVarianceDays'),
        onTime: num(r, 'OnTimePct'),
        ber: num(r, 'BerPct'),
      })),
    [tat.rows]
  );
  const tatSummary = useMemo(() => {
    const repairs = tat.rows.reduce((s, r) => s + num(r, 'Repairs'), 0);
    const totalOnTime = tat.rows.reduce((s, r) => s + num(r, 'OnTimePct') * num(r, 'Repairs'), 0);
    const late = tatRows.filter((r) => r.variance > 0).length;
    return { repairs, onTimeRate: repairs ? totalOnTime / repairs : 0, late };
  }, [tat.rows, tatRows]);

  return (
    <KpiPage kpi={kpi} window={window} onWindowChange={setWindow} hideMethodology>
      <Stack gap="md">
        {/* ============================ SECTION 1 — PRICING BENCHMARK =========================== */}
        <SectionHeading
          title="1 · Repair pricing vs the market"
          subtitle="We can't compare a vendor to its own estimate — instead each vendor's actual repair cost is benchmarked against every other signal we have for that same part & condition: our paid invoices to other vendors, plus the RFQ repair quotes. Above the benchmark = expensive; below = cheap."
        />

        <QueryState isLoading={benchmark.isLoading} error={benchmark.error} isEmpty={!vendors.length}>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatTile label="Vendors benchmarked" value={fmtInt(benchTotals.vendorCount)} sub="5+ comparable repairs each" color={c.series[0]} />
            <StatTile label="Comparable repairs" value={fmtInt(benchTotals.repairs)} sub="On parts with ≥2 vendors priced" />
            <StatTile label="Flagged repairs" value={fmtInt(benchTotals.flagged)} sub=">25% above or below benchmark" color={c.series[3]} />
            <StatTile label="Paid over benchmark" value={fmtMoney(benchTotals.dollarsOver)} sub="Net $ above the market signal" color={c.negative} />
          </SimpleGrid>
        </QueryState>

        <ChartCard
          title="Average deviation from benchmark, by vendor"
          caption="Vendors with the most comparable repairs. Red = pays above the per-part benchmark (expensive); blue = below. A high positive average across many repairs is a systematic pricing signal, not a one-off."
          height={360}
        >
          <QueryState isLoading={benchmark.isLoading} error={benchmark.error} isEmpty={!devByVendor.length}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={devByVendor} layout="vertical" margin={{ top: 8, right: 24, left: 60, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} horizontal={false} />
                <XAxis type="number" stroke={c.axis} fontSize={12} tickFormatter={(v) => `${Math.round(v)}%`} />
                <YAxis type="category" dataKey="vendor" stroke={c.axis} fontSize={10} width={150} />
                <ReferenceLine x={0} stroke={c.axis} />
                <Tooltip
                  content={<ChartTooltip formatter={(v) => fmtPct(v)} />}
                  cursor={{ fill: c.grid, opacity: 0.3 }}
                  wrapperStyle={{ outline: 'none' }}
                />
                <Bar dataKey="avgDev" name="Avg deviation %" radius={[0, 4, 4, 0]}>
                  {devByVendor.map((d) => (
                    <Cell key={d.vendor} fill={d.avgDev > 0 ? c.negative : c.series[0]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </QueryState>
        </ChartCard>

        {/* ============================ SECTION 2 — CONFLICTING SIGNALS ========================= */}
        <SectionHeading
          title="2 · Conflicting signals"
          subtitle="The specific repairs where what we paid a vendor is far from what others charged or quoted for that same part — the cases worth a look."
        />

        <Card withBorder radius="md" p={0}>
          <QueryState isLoading={conflicts.isLoading} error={conflicts.error} isEmpty={!conflictRows.length}>
            <ScrollArea.Autosize mah={460}>
              <Table striped highlightOnHover stickyHeader verticalSpacing="xs" fz="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Vendor</Table.Th>
                    <Table.Th>Part</Table.Th>
                    <Table.Th ta="center">Cond</Table.Th>
                    <Table.Th ta="right">We paid</Table.Th>
                    <Table.Th ta="right">Benchmark</Table.Th>
                    <Table.Th ta="right">Deviation</Table.Th>
                    <Table.Th ta="right">Others priced</Table.Th>
                    <Table.Th ta="right">When</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {conflictRows.map((r, i) => (
                    <Table.Tr key={`${r.vendor}-${r.part}-${i}`}>
                      <Table.Td>
                        <Text size="xs" lineClamp={1}>
                          {r.vendor}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text ff="monospace" size="xs">
                          {r.part}
                        </Text>
                        <Text size="xs" c="dimmed" lineClamp={1}>
                          {r.desc}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="center">
                        <Badge size="sm" variant="light" color="gray">
                          {r.cond}
                        </Badge>
                      </Table.Td>
                      <Table.Td ta="right">{fmtMoney(r.vendorCost)}</Table.Td>
                      <Table.Td ta="right">{fmtMoney(r.benchmark)}</Table.Td>
                      <Table.Td ta="right">
                        <Text size="xs" fw={600} c={r.dev > 0 ? c.negative : c.series[0]}>
                          {r.dev > 0 ? '+' : ''}
                          {fmtPct(r.dev)}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="xs" c="dimmed">
                          {fmtInt(r.priced)} · {fmtMoney(r.minP)}–{fmtMoney(r.maxP)}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="xs" c="dimmed">
                          {r.when}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          </QueryState>
        </Card>
        <Text size="xs" c="dimmed">
          Benchmark = median of all paid + RFQ repair-price signals for that part & condition, on parts with ≥2
          vendors. The pool mixes paid invoices with RFQ quotes, so a paid cost far above a quote-heavy benchmark
          is a &quot;why did we pay this?&quot; flag — surfaced for review, not an automatic verdict.
        </Text>

        {/* ============================ SECTION 3 — TURN TIME (secondary) ======================= */}
        <SectionHeading
          title="3 · Turn-time reliability"
          subtitle="Separate from pricing: did the vendor return the unit on time, and how often did it come back beyond economical repair (BER)?"
        />

        <QueryState isLoading={tat.isLoading} error={tat.error} isEmpty={!tatRows.length}>
          <SimpleGrid cols={{ base: 2, md: 3 }} spacing="md">
            <StatTile label="Repairs measured" value={fmtInt(tatSummary.repairs)} sub="Vendors with 20+ repairs" />
            <StatTile label="On-time rate" value={fmtPct(tatSummary.onTimeRate)} sub="Returned by the promised date" color={c.positive} />
            <StatTile label="Vendors running late" value={fmtInt(tatSummary.late)} sub="Avg actual TAT over promised" color={c.series[3]} />
          </SimpleGrid>
        </QueryState>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <ChartCard title="Turn time: promised vs actual" caption="Average days from order to return. Bars above the promised line are chronic overruns.">
            <QueryState isLoading={tat.isLoading} error={tat.error} isEmpty={!tatRows.length}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tatRows} margin={{ top: 8, right: 8, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                  <XAxis dataKey="vendor" stroke={c.axis} fontSize={9} angle={-25} textAnchor="end" interval={0} />
                  <YAxis stroke={c.axis} fontSize={12} width={40} />
                  <Tooltip content={<ChartTooltip formatter={(v) => `${fmtInt(v)} days`} />} cursor={{ fill: c.grid, opacity: 0.3 }} wrapperStyle={{ outline: 'none' }} />
                  <Bar dataKey="promised" name="Promised" fill={c.neutral} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="actual" name="Actual" fill={c.series[0]} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>

          <ChartCard title="On-time and BER rate by vendor" caption="On-time share (higher is better) against beyond-economical-repair rate (lower is better).">
            <QueryState isLoading={tat.isLoading} error={tat.error} isEmpty={!tatRows.length}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tatRows} margin={{ top: 8, right: 8, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                  <XAxis dataKey="vendor" stroke={c.axis} fontSize={9} angle={-25} textAnchor="end" interval={0} />
                  <YAxis stroke={c.axis} fontSize={12} width={40} tickFormatter={(v) => `${Math.round(v)}%`} />
                  <Tooltip content={<ChartTooltip formatter={(v) => fmtPct(v)} />} cursor={{ fill: c.grid, opacity: 0.3 }} wrapperStyle={{ outline: 'none' }} />
                  <Bar dataKey="onTime" name="On-time %" fill={c.positive} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="ber" name="BER %" fill={c.negative} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>
        </SimpleGrid>
      </Stack>
    </KpiPage>
  );
}
